// ============================================
// MODIFY HOOKS
// Combines hooks from:
// - [RecommendModify.tsx] RecommendModify (MODIFY 650-662)
// - [ModifyFields.tsx] -> [RecommendModify.tsx] (MODIFY 663)
// Note: Most hooks in RecommendModify.tsx and ModifyFields.tsx are inline
// useEffect hooks tightly coupled to their components and cannot be extracted.
// Only recommendNoteToFields is a pure function that can be moved here.
// ============================================

import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {modelPortfolioField} from '../RecommendModify/modifyFieldTypes';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {Pot} from '../../../schema-types/Pot';
import {getPlanFeesFormikFromServer} from '../CreateRecommend/PlanFees';

export enum modifyChanges {
  feeChange = 'Change in fee',
  mandateChange = 'Change in mandate',
  regularContributionChange = 'Change in regular contribution',
  regularWithdrawalChange = 'Change in regular withdrawal',
  changeOfOwnership = 'Change in ownership',
}

export function recommendNoteToFields(
  recommendation: OVRecommendation,
  sourcePots: Pot[]
): recommendFieldsType['modify'] {
  if ((recommendation.action ?? undefined) === OVRecommendActionEnum.MODIFY) {
    let noteFull = String(recommendation.note || '');
    const pot = recommendation.pots?.length ? recommendation.pots[0] : null;
    const sourcePot = pot?.id ? sourcePots.find((x) => x.id === pot?.id) : null;
    const feeChange = noteFull?.includes(modifyChanges.feeChange);
    if (feeChange) {
      noteFull = noteFull.replaceAll(modifyChanges.feeChange, '');
    }
    const mandateChange =
      noteFull?.includes(modifyChanges.mandateChange) ||
      !!(sourcePot?.mandate || sourcePot?.modelPortfolio?.id);

    const planFees =
      mandateChange && recommendation?.pots?.length
        ? getPlanFeesFormikFromServer(recommendation?.pots[0])
        : null;

    if (mandateChange) {
      noteFull = noteFull.replaceAll(modifyChanges.mandateChange, '');
    }
    const regularContributionChange = noteFull?.includes(
      modifyChanges.regularContributionChange
    );
    if (regularContributionChange) {
      noteFull = noteFull.replaceAll(modifyChanges.regularContributionChange, '');
    }
    const regularWithdrawalChange = noteFull?.includes(
      modifyChanges.regularWithdrawalChange
    );
    if (regularWithdrawalChange) {
      noteFull = noteFull.replaceAll(modifyChanges.regularWithdrawalChange, '');
    }
    const changeOfOwnership = noteFull?.includes(modifyChanges.changeOfOwnership);
    if (changeOfOwnership) {
      noteFull = noteFull.replaceAll(modifyChanges.changeOfOwnership, '');
    }
    const model: modelPortfolioField | null = pot?.modelPortfolio
      ? {
          ...pot?.modelPortfolio,
          providerContactLastName:
            pot.modelPortfolio.providerContact?.lastName || undefined,
          providerContactJobTitle:
            pot.modelPortfolio.providerContact?.jobTitle || undefined,
        }
      : null;

    return {
      feeChange,
      mandateChange,
      note: noteFull.trim(),
      regularContributionChange,
      regularWithdrawalChange,
      changeOfOwnership,
      source: {
        id: recommendation?.pots?.length ? recommendation.pots[0].pot?.id || '' : '',
      },
      model: pot?.mandate || model || undefined,
      planFees,
    };
  }

  return {
    feeChange: false,
    mandateChange: false,
    regularContributionChange: false,
    regularWithdrawalChange: false,
    changeOfOwnership: false,
    note: '',
    source: {
      id: '',
    },
    model: undefined,
  };
}
